import React, { useState, useEffect } from 'react';
import { Box, Spinner, Alert } from '@cloudscape-design/components';
import Plot from 'react-plotly.js';
import apiService from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

/**
 * MarketContextChart — AMZN historical share price chart for the
 * Data Storytelling / Report page.
 *
 * Provides factual market context alongside document research findings.
 * Includes a clear disclaimer: this is NOT investment advice.
 */
function MarketContextChart() {
  const { isDark } = useTheme();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService.getMarketChartData('AMZN', '2022-01-01', '2025-03-31')
      .then(data => {
        if (!data) {
          setError('Market data endpoint returned no data');
        } else {
          setChartData(data);
        }
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'Failed to load market data');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="normal" />
        <Box marginTop="xs" color="text-body-secondary" fontSize="body-s">
          Loading market context...
        </Box>
      </Box>
    );
  }

  if (error && !chartData) {
    return (
      <Alert type="warning" header="Market context unavailable">
        {error}
      </Alert>
    );
  }

  if (!chartData || !chartData.x || chartData.x.length === 0) {
    return (
      <Alert type="warning" header="Market context unavailable">
        {error || 'No price data returned. Check backend logs for /api/v2/market-data/chart/AMZN'}
      </Alert>
    );
  }

  const bgColor = isDark ? '#1e293b' : '#ffffff';
  const plotBg = isDark ? '#0f172a' : '#f8f9fa';
  const textColor = isDark ? '#e2e8f0' : '#232f3e';
  const gridColor = isDark ? '#334155' : '#e2e8f0';

  // Build event annotation markers
  const annotations = (chartData.events || []).map(event => ({
    x: event.date,
    y: chartData.y[chartData.x.indexOf(event.date)] || 0,
    xref: 'x',
    yref: 'y',
    text: '📊',
    showarrow: true,
    arrowhead: 2,
    arrowsize: 0.8,
    arrowcolor: isDark ? '#60a5fa' : '#0073bb',
    ax: 0,
    ay: -30,
    font: { size: 12 },
    hovertext: event.event,
  }));

  const layout = {
    autosize: true,
    height: 280,
    margin: { l: 55, r: 20, t: 40, b: 50 },
    paper_bgcolor: bgColor,
    plot_bgcolor: plotBg,
    font: { family: 'Amazon Ember, Helvetica Neue, Arial, sans-serif', size: 11, color: textColor },
    title: {
      text: 'AMZN Share Price — 2022 to Q1 2025',
      font: { size: 14, color: textColor },
      x: 0.5,
      xanchor: 'center',
    },
    xaxis: {
      title: { text: 'Date', font: { size: 11 } },
      gridcolor: gridColor,
      linecolor: gridColor,
      tickfont: { color: textColor },
    },
    yaxis: {
      title: { text: 'Price (USD)', font: { size: 11 } },
      gridcolor: gridColor,
      linecolor: gridColor,
      tickfont: { color: textColor },
      tickprefix: '$',
    },
    annotations,
    showlegend: false,
  };

  const trace = {
    x: chartData.x,
    y: chartData.y,
    type: 'scatter',
    mode: 'lines',
    line: { color: isDark ? '#60a5fa' : '#1f77b4', width: 2 },
    hovertemplate: '%{x}<br>$%{y:.2f}<extra></extra>',
  };

  return (
    <div>
      <Plot
        data={[trace]}
        layout={layout}
        config={{ displayModeBar: false, responsive: true }}
        style={{ width: '100%' }}
        useResizeHandler
      />
      <Box fontSize="body-s" color="text-body-secondary" textAlign="center" padding={{ top: 'xs' }}>
        📊 Market price data shown for contextual commentary only.
        This is not investment advice or a financial recommendation.
      </Box>
    </div>
  );
}

export default MarketContextChart;
